import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import { LANGUAGE, LANGUAGE_EN, LANGUAGE_FR, tt } from "../../../../utils/i18n/i18n";
import React, { LegacyRef, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { updateCustomerThunk } from "../../../../redux/actions/user";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { useDispatch } from "react-redux";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../../designsystem/ds_commons";
import KC_Input from "../../../../designsystem/KC_FormInput/KC_Input";
import KC_DateTimePicker from "../../../../designsystem/KC_FormInput/KC_DateTimePicker";
import TSUtils from "../../../../utils/helpers/TSUtils";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import { getCountriesByLanguage } from "../../../../utils/i18n/getCountriesByLang";
import { format, isToday } from "date-fns";
import BusinessUser from "@tootsweet/model/entities/BusinessUser";

interface Props {
  activeDate: Date;
  blockCustomerForm: boolean;
  setBlockCustomerForm: (block: boolean) => void;
  onDateChange: (date: Date) => void;
  isUpsell: boolean;
  isNew?: boolean;
}

export default function (props: Props) {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const handleSubmit = async (values: BusinessUser) => {
    if (!accountToDisplay?.trackingID || !accountToDisplay?.token) {
      return;
    }

    if (!!values.companyAddress) {
      props.setBlockCustomerForm(true);
    }

    dispatch(
      updateCustomerThunk(
        values,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const [customDateText, setCustomDateText] = useState("");

  const CustomDateInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
    ({ value, onClick, disabled }, ref: LegacyRef<HTMLInputElement>) => {
      const displayText = isToday(props.activeDate)
        ? tt("start_immediately")
        : format(props.activeDate, tt("date_format_datepicker"));

      return (
        <input
          ref={ref}
          className="w-100"
          onClick={onClick}
          value={displayText}
          onChange={(e) => {
            setCustomDateText(e.target.value);
          }}
          disabled={disabled}
          autoComplete="off"
        />
      );
    }
  );

  const { ALL_COUNTRIES_NAME, ALL_COUNTRIES_CODE } = useMemo(() => {
    return getCountriesByLanguage();
  }, []);

  if (!accountToDisplay?.businessUser) {
    return null;
  }

  return (
    <>
      <KC_Heading3 text={tt("billing_information")} />
      <Formik<BusinessUser>
        initialValues={accountToDisplay?.businessUser}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form
              id="saas-checkout-form"
              className="flex-grow-1"
              autoComplete="off"
            >
              <div className="d-flex flex-column h-100 gap-sm">
                <div className="w-100 d-flex st-gap flex-column flex-md-row">
                  <div className="w-100">
                    <label htmlFor={"firstName"}>
                      <KC_UIText
                        weight={KC_Weights.md}
                        text={tt("jp_onboarding_form_label_first_name")}
                      />
                    </label>
                    <KC_Input
                      id="firstName"
                      htmlFor="firstName"
                      placeholder={tt("jp_onboarding_form_label_first_name")}
                      type="text"
                      setFieldValue={setFieldValue}
                      required={true}
                      value={values?.firstName ? values.firstName : ""}
                      disabled={props.blockCustomerForm}
                      autoComplete="off"
                    />
                  </div>
                  <div className="w-100">
                    <label htmlFor={"lastName"}>
                      <KC_UIText
                        weight={KC_Weights.md}
                        text={tt("jp_onboarding_form_label_last_name")}
                      />
                    </label>
                    <KC_Input
                      id="lastName"
                      htmlFor="lastName"
                      placeholder={tt("jp_onboarding_form_label_last_name")}
                      type="text"
                      setFieldValue={setFieldValue}
                      required={true}
                      value={values?.lastName ? values.lastName : ""}
                      disabled={props.blockCustomerForm}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="w-100 d-flex st-gap flex-column flex-md-row">
                  <div className="w-100">
                    <KC_UIText
                      weight={KC_Weights.bold}
                      text={tt("jetpack_checkout_form_company_optional")}
                    />
                    <KC_Input
                      id="companyName"
                      setFieldValue={setFieldValue}
                      htmlFor={"companyName"}
                      value={values.companyName || ""}
                      autoComplete="off"
                    />
                  </div>
                  <div className="w-100 d-flex flex-column ds-gap-column-standard">
                    <label>
                      <KC_UIText
                        weight={KC_Weights.bold}
                        text={tt("billing_country")}
                      />
                    </label>
                    <div className="kc-input-wrapper">
                      <select
                        id="billingCountry"
                        className="kc_input pl-3"
                        disabled={props.blockCustomerForm}
                        required={true}
                        value={accountToDisplay?.businessUser?.billingCountry}
                        autoComplete="off"
                        onChange={(e) => {
                          void handleSubmit({
                            ...values,
                            billingCountry: e.target.value
                          });
                        }}
                      >
                        <option value="">
                          {tt(
                            "jetpack_checkout_form_billing_country_placeholder"
                          )}
                        </option>
                        {ALL_COUNTRIES_NAME.map((name, idx) => {
                          const val = ALL_COUNTRIES_CODE[idx];
                          return (
                            <option key={`country-${idx}-${val}`} value={val}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="w-100 d-flex flex-column">
                  <label>
                    <KC_UIText
                      weight={KC_Weights.bold}
                      text={tt("billing_address_label")}
                    />
                  </label>
                  <KC_Input
                    id="companyAddress"
                    htmlFor="companyAddress"
                    type="text"
                    setFieldValue={setFieldValue}
                    required={true}
                    value={values?.companyAddress ? values.companyAddress : ""}
                    disabled={props.blockCustomerForm}
                    autoComplete="off"
                  />
                </div>
                <div className="w-100 d-flex flex-column">
                  <label>
                    <KC_UIText
                      weight={KC_Weights.bold}
                      text={tt("subscription_start_date_label")}
                    />
                  </label>
                  <KC_DateTimePicker
                    dateFormat={tt("date_format_datepicker")}
                    filterDate={TSUtils.isGreaterThanToday}
                    onChange={(date) => {
                      if (date) {
                        props.onDateChange(date);
                      }
                    }}
                    activeDate={props.activeDate}
                    customInput={<CustomDateInput disabled={props.isUpsell} />}
                    disabled={props.isUpsell}
                    locale={LANGUAGE === LANGUAGE_FR ? LANGUAGE_FR : LANGUAGE_EN}
                  />
                </div>
                {!props.blockCustomerForm && (
                  <div className="d-flex w-100 st-gap flex-grow-1 align-items-end">
                    {/**
                     Dummy button in order to make both sides
                     equal in width. Does not work with a dummy div,
                     nor with an invisible span inside the dummy div.
                     **/}
                    <KC_Button
                      type="button"
                      disabled={true}
                      text={
                        props.isNew
                          ? tt("new_venue_add")
                          : tt("subscribe_button")
                      }
                      size={KC_ButtonSize.Large}
                      className="invisible d-none d-sm-block w-100"
                    />
                    <KC_Button
                      id="proceed_to_payment"
                      type="submit"
                      text={
                        props.isNew
                          ? tt("new_venue_add")
                          : tt("subscribe_button")
                      }
                      size={KC_ButtonSize.Large}
                      className="d-flex w-100"
                    />
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

type CustomInputProps = {
  value?: string;
  onClick?: () => void;
  disabled?: boolean;
};
